import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api, findCurrency, fmtMoney } from "@/lib/api";
import { PREVIEW } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

// Module-scope constant so ReactMarkdown doesn't see a new array reference
// on every render (prevents unnecessary re-renders of the markdown subtree).
const REMARK_PLUGINS = [remarkGfm];
import {
  ArrowLeft,
  Copy,
  Download,
  FileType2,
  Loader2,
  Save,
} from "lucide-react";

export default function ProposalPreview() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isDraft = location.pathname === "/proposals/draft/preview";

  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!isDraft);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const cRes = await api.get("/currencies");
        setCurrencies(cRes.data.currencies);
        if (isDraft) {
          const raw = sessionStorage.getItem("imali:draft");
          if (!raw) {
            toast.error("No draft found. Please generate a proposal first.");
            navigate("/proposals/new");
            return;
          }
          setProposal(JSON.parse(raw));
        } else {
          const { data } = await api.get(`/proposals/${id}`);
          setProposal(data);
        }
      } catch (e) {
        toast.error("Unable to load proposal.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isDraft, navigate]);

  // Live update while streaming from ProposalForm
  useEffect(() => {
    if (!isDraft) return;
    const onUpdate = (e) => setProposal(e.detail || null);
    window.addEventListener("imali:draft-update", onUpdate);
    return () => window.removeEventListener("imali:draft-update", onUpdate);
  }, [isDraft]);

  const currency = proposal ? findCurrency(currencies, proposal.currency_code) : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(proposal.content);
      toast.success("Proposal copied to clipboard");
    } catch {
      toast.error("Unable to copy — try selecting the text manually");
    }
  };

  const downloadBlob = async (format) => {
    try {
      const res = await api.post(
        `/export/${format}`,
        { title: proposal.title, content: proposal.content },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${proposal.title.replace(/[^\w\-. ]+/g, "_")}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch {
      toast.error(`Unable to download ${format.toUpperCase()}`);
    }
  };

  const save = async () => {
    if (!isDraft || saving) return;
    setSaving(true);
    try {
      const payload = {
        type: proposal.type,
        title: proposal.title,
        industry_id: proposal.industry_id || null,
        business_name: proposal.business_name,
        amount: proposal.amount,
        currency_code: proposal.currency_code,
        content: proposal.content,
      };
      const { data } = await api.post("/proposals", payload);
      sessionStorage.removeItem("imali:draft");
      setSaved(true);
      toast.success("Proposal saved");
      navigate(`/proposals/${data.id}`, { replace: true });
    } catch {
      toast.error("Unable to save proposal");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !proposal) {
    return (
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading proposal…
      </div>
    );
  }

  return (
    <div data-testid={PREVIEW.page} className="bg-muted/40 min-h-[calc(100vh-4rem)] pb-16">
      {/* Action bar */}
      <div className="sticky top-16 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center justify-between gap-3">
          <Button
            data-testid={PREVIEW.backBtn}
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft aria-hidden="true" className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              data-testid={PREVIEW.copyBtn}
              variant="outline"
              size="sm"
              onClick={copy}
              className="rounded-full"
            >
              <Copy aria-hidden="true" className="w-4 h-4 mr-2" /> Copy
            </Button>
            <Button
              data-testid={PREVIEW.downloadPdf}
              variant="outline"
              size="sm"
              onClick={() => downloadBlob("pdf")}
              className="rounded-full"
            >
              <Download aria-hidden="true" className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              data-testid={PREVIEW.downloadDocx}
              variant="outline"
              size="sm"
              onClick={() => downloadBlob("docx")}
              className="rounded-full"
            >
              <FileType2 aria-hidden="true" className="w-4 h-4 mr-2" /> DOCX
            </Button>
            {isDraft && !saved && (
              <Button
                data-testid={PREVIEW.saveBtn}
                size="sm"
                onClick={save}
                disabled={saving || proposal?.streaming}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {saving ? (
                  <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save aria-hidden="true" className="w-4 h-4 mr-2" />
                )}
                {proposal?.streaming ? "Drafting…" : "Save"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Paper */}
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-10">
        <div className="rounded-2xl bg-background border border-border shadow-sm">
          <div className="px-8 lg:px-14 pt-12 pb-4 border-b border-border">
            <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold">
              {proposal.type === "loan" ? "Business Loan Proposal" : "Grant Proposal"}
            </p>
            <h1
              data-testid={PREVIEW.title}
              className="mt-3 font-display text-3xl lg:text-4xl font-light tracking-tighter leading-tight"
            >
              {proposal.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground font-mono">
              <span>Amount · {fmtMoney(proposal.amount, currency)}</span>
              <span>Applicant · {proposal.business_name}</span>
              {proposal.industry_id && (
                <span className="uppercase tracking-widest">
                  {proposal.industry_id}
                </span>
              )}
            </div>
          </div>

          <article
            data-testid={PREVIEW.content}
            className="imali-prose px-8 lg:px-14 py-10"
          >
            {proposal.streaming && !proposal.content && (
              <div className="flex items-center gap-3 text-muted-foreground py-4">
                <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />
                <span className="text-sm">
                  Imali is drafting your proposal — content will appear here
                  token by token…
                </span>
              </div>
            )}
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
              {proposal.content || ""}
            </ReactMarkdown>
            {proposal.streaming && proposal.content && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 aria-hidden="true" className="w-3 h-3 animate-spin" />
                <span>Streaming…</span>
              </div>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}
